import { useMemo, useState } from 'react';
import moment from 'moment';
import {
  CATEGORY_META,
  RESOURCE_LABELS,
  applyTimeToToday,
  defaultSessionTitle,
  formatClockTime,
  formatCountdown,
  formatDurationMinutes,
  getScheduleWarning,
  normalizeScheduleResult,
  toTimeInputValue,
} from '../../utils/cookingSchedule';
import {
  getStepProgress,
  getStepRemainingMs,
  useCookingSession,
} from '../../hooks/useCookingSession';

function CategoryBadge({ category }) {
  const meta = CATEGORY_META[category] || CATEGORY_META.prep;
  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${meta.badge}`}>
      {meta.label}
    </span>
  );
}

function StepProgressBar({ step, state, now }) {
  const meta = CATEGORY_META[step.category] || CATEGORY_META.prep;
  const progress = getStepProgress(step, state, now);
  return (
    <div className="h-2 rounded-full bg-white/70 overflow-hidden">
      <div className={`h-full rounded-full transition-all ${meta.bar}`} style={{ width: `${progress}%` }} />
    </div>
  );
}

function ActiveTimerCard({ step, state, now, onPause, onResume, onComplete }) {
  const meta = CATEGORY_META[step.category] || CATEGORY_META.prep;
  const remaining = getStepRemainingMs(step, state, now);
  const isPaused = state.status === 'paused';
  const isPassive = step.kind === 'passive';

  return (
    <div className={`rounded-xl border p-4 shadow-sm ${meta.card}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            {step.recipeTitle}
          </p>
          <p className="font-semibold text-gray-900">{step.label}</p>
        </div>
        <CategoryBadge category={step.category} />
      </div>
      <p className="text-4xl font-bold tabular-nums text-gray-900 mb-3">
        {formatCountdown(remaining)}
      </p>
      <StepProgressBar step={step} state={state} now={now} />
      <p className="text-xs text-gray-500 mt-2">
        {formatCountdown(remaining)} / {formatDurationMinutes(step.duration)}
      </p>
      {!isPassive && (
        <div className="flex gap-2 mt-4">
          {isPaused ? (
            <button
              type="button"
              onClick={() => onResume(step.id)}
              className="flex-1 px-3 py-2 rounded-lg bg-white border border-gray-300 text-sm font-semibold hover:bg-gray-50"
            >
              Resume
            </button>
          ) : (
            <button
              type="button"
              onClick={() => onPause(step.id)}
              className="flex-1 px-3 py-2 rounded-lg bg-white border border-gray-300 text-sm font-semibold hover:bg-gray-50"
            >
              Pause
            </button>
          )}
          <button
            type="button"
            onClick={() => onComplete(step.id)}
            className="flex-1 px-3 py-2 rounded-lg bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700"
          >
            Done
          </button>
        </div>
      )}
      {isPassive && remaining === 0 && (
        <p className="text-sm font-medium text-amber-800 mt-2">
          Timer finished — tap Done when ready.
        </p>
      )}
      {isPassive && (
        <button
          type="button"
          onClick={() => onComplete(step.id)}
          className="mt-3 w-full px-3 py-2 rounded-lg bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700"
        >
          Done
        </button>
      )}
    </div>
  );
}

function TimelineStepCard({
  step,
  state,
  now,
  sessionActive,
  waitReason,
  onComplete,
}) {
  const meta = CATEGORY_META[step.category] || CATEGORY_META.prep;
  const status = state?.status || 'pending';
  const isActive = status === 'active' || status === 'paused';
  const isCompleted = status === 'completed';
  const isPending = status === 'pending';
  const remaining = isActive ? getStepRemainingMs(step, state, now) : step.duration * 60 * 1000;

  return (
    <div
      className={`rounded-xl border p-4 shadow-sm ${
        isCompleted ? 'border-gray-200 bg-gray-50 opacity-70' : meta.card
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-semibold text-gray-500">{step.recipeTitle}</p>
          <p className={`font-semibold ${isCompleted ? 'line-through text-gray-500' : 'text-gray-900'}`}>
            {step.label}
          </p>
          <p className="text-sm text-gray-600 mt-1">
            {formatClockTime(step.plannedStartAt)} – {formatClockTime(step.plannedEndAt)}
          </p>
        </div>
        <CategoryBadge category={step.category} />
      </div>

      {sessionActive && isPending && !waitReason && (
        <p className="text-xs text-gray-500 mt-2">Scheduled for later in the cooking plan.</p>
      )}

      {sessionActive && isPending && waitReason && (
        <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-2">
          {waitReason}
        </p>
      )}

      {isActive && (
        <div className="mt-3">
          <p className="text-2xl font-bold tabular-nums">{formatCountdown(remaining)}</p>
          <StepProgressBar step={step} state={state} now={now} />
        </div>
      )}

      {sessionActive && isActive && step.kind === 'active' && (
        <button
          type="button"
          onClick={() => onComplete(step.id)}
          className="mt-3 px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-semibold hover:bg-gray-50"
        >
          Mark done
        </button>
      )}

      {sessionActive && isActive && step.kind === 'passive' && (
        <>
          {remaining === 0 && (
            <p className="text-sm font-medium text-amber-800 mt-2">
              Timer finished — tap Done when ready.
            </p>
          )}
          <button
            type="button"
            onClick={() => onComplete(step.id)}
            className="mt-3 px-4 py-2 rounded-lg bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700"
          >
            Done
          </button>
        </>
      )}
    </div>
  );
}

function EquipmentPanel({ equipmentStatus, stepStates, now }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
      <h3 className="font-bold text-gray-900 mb-3">Equipment</h3>
      <div className="space-y-3">
        {equipmentStatus.map(({ resource, busy, capacity, inUse, tasks }) => (
          <div key={resource} className="rounded-lg border border-gray-200 p-3">
            <div className="flex items-center justify-between gap-2">
              <p className="font-semibold text-gray-900">{RESOURCE_LABELS[resource] || resource}</p>
              <span
                className={`text-xs font-semibold px-2 py-1 rounded-full ${
                  inUse >= capacity ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'
                }`}
              >
                {inUse}/{capacity} in use
              </span>
            </div>
            {busy ? (
              <div className="mt-2 space-y-2">
                {tasks.map((task) => {
                  const state = stepStates[task.id];
                  const remaining = getStepRemainingMs(task, state, now);
                  return (
                    <div key={task.id}>
                      <p className="text-sm text-gray-700">{task.label}</p>
                      <p className="text-xs text-gray-500">
                        Until {formatClockTime(task.plannedEndAt)} · {formatCountdown(remaining)} left
                      </p>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-gray-500 mt-1">Free</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function CookingSessionView({ scheduleResult, onBack }) {
  const steps = useMemo(
    () => normalizeScheduleResult(scheduleResult),
    [scheduleResult]
  );
  const totalMinutes = scheduleResult.total_minutes;
  const recipes = scheduleResult.recipes || [];

  const [sessionTitle, setSessionTitle] = useState(() => defaultSessionTitle(recipes));
  const [anchorMode, setAnchorMode] = useState('serve');
  const [anchorTime, setAnchorTime] = useState(() =>
    toTimeInputValue(moment().add(2, 'hours').toDate())
  );
  const [viewMode, setViewMode] = useState('timeline');

  const anchorDate = useMemo(
    () => applyTimeToToday(anchorTime),
    [anchorTime]
  );

  const warning = useMemo(
    () => getScheduleWarning(totalMinutes, anchorMode, anchorDate),
    [totalMinutes, anchorMode, anchorDate]
  );

  const {
    sessionActive,
    stepStates,
    anchoredSteps,
    startAt,
    serveAt,
    now,
    activeSteps,
    upcomingSteps,
    waitingSteps,
    completedCount,
    equipmentStatus,
    startSession,
    pauseStep,
    resumeStep,
    completeStep,
    getResourceBlockReason,
  } = useCookingSession(steps, totalMinutes, anchorMode, anchorDate);

  const liveServeAt = useMemo(() => {
    if (!sessionActive) {
      return serveAt;
    }
    const base = anchoredSteps[0]?.plannedStartAt
      ? new Date(anchoredSteps[0].plannedStartAt.getTime() - anchoredSteps[0].start * 60 * 1000)
      : serveAt;
    return new Date(base.getTime() + totalMinutes * 60 * 1000);
  }, [sessionActive, serveAt, anchoredSteps, totalMinutes]);

  const previewUpcoming = useMemo(
    () => [...anchoredSteps].sort((a, b) => a.start - b.start).slice(0, 6),
    [anchoredSteps]
  );

  const timelineMarkers = useMemo(() => {
    const markers = new Set([formatClockTime(startAt), formatClockTime(serveAt)]);
    anchoredSteps.forEach((step) => {
      markers.add(formatClockTime(step.plannedStartAt));
    });
    return [...markers].sort();
  }, [anchoredSteps, startAt, serveAt]);

  const recipeSummary =
    recipes.length <= 3
      ? recipes.map((recipe) => recipe.title).join(' · ')
      : `${recipes.length} recipes`;

  const activeTimersPanel = (
    <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
      <h3 className="font-bold text-gray-900 mb-3">
        Active timers ({activeSteps.length})
      </h3>
      {activeSteps.length === 0 ? (
        <p className="text-sm text-gray-500">
          {sessionActive
            ? 'No active steps right now.'
            : 'Start the session to run step timers.'}
        </p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-1 gap-3">
          {activeSteps.map((step) => (
            <ActiveTimerCard
              key={step.id}
              step={step}
              state={stepStates[step.id]}
              now={now}
              onPause={pauseStep}
              onResume={resumeStep}
              onComplete={completeStep}
            />
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl shadow-lg p-3 sm:p-4">
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-3">
          <button
            type="button"
            onClick={onBack}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-semibold hover:bg-gray-50"
          >
            Back
          </button>
          <input
            type="text"
            value={sessionTitle}
            onChange={(event) => setSessionTitle(event.target.value)}
            className="min-w-0 flex-1 text-base font-semibold border border-gray-200 rounded-lg px-2.5 py-1.5"
            title={recipes.map((recipe) => recipe.title).join(' · ')}
          />
          <div className="flex rounded-lg border border-gray-200 overflow-hidden shrink-0">
            <button
              type="button"
              onClick={() => setViewMode('overview')}
              className={`px-3 py-1.5 text-xs font-semibold ${
                viewMode === 'overview' ? 'bg-amber-600 text-white' : 'bg-white text-gray-700'
              }`}
            >
              Overview
            </button>
            <button
              type="button"
              onClick={() => setViewMode('timeline')}
              className={`px-3 py-1.5 text-xs font-semibold ${
                viewMode === 'timeline' ? 'bg-amber-600 text-white' : 'bg-white text-gray-700'
              }`}
            >
              Timeline
            </button>
          </div>
          {!sessionActive ? (
            <button
              type="button"
              onClick={startSession}
              className="px-4 py-1.5 bg-amber-600 text-white rounded-lg text-sm font-semibold hover:bg-amber-700 shrink-0"
            >
              Start cooking
            </button>
          ) : (
            <span className="px-3 py-1.5 rounded-lg bg-emerald-100 text-emerald-800 text-xs font-semibold shrink-0">
              In progress
            </span>
          )}
        </div>

        <p className="text-xs text-gray-500 mb-3 truncate" title={recipes.map((recipe) => recipe.title).join(' · ')}>
          {recipeSummary}
        </p>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setAnchorMode('serve')}
              className={`px-2.5 py-1 rounded-md text-xs font-semibold ${
                anchorMode === 'serve'
                  ? 'bg-amber-600 text-white'
                  : 'border border-gray-300 text-gray-700'
              }`}
            >
              Serve time
            </button>
            <button
              type="button"
              onClick={() => {
                setAnchorMode('start');
                setAnchorTime(toTimeInputValue(new Date()));
              }}
              className={`px-2.5 py-1 rounded-md text-xs font-semibold ${
                anchorMode === 'start'
                  ? 'bg-amber-600 text-white'
                  : 'border border-gray-300 text-gray-700'
              }`}
            >
              Start time
            </button>
            <input
              type="time"
              value={anchorTime}
              onChange={(event) => setAnchorTime(event.target.value)}
              className="p-1 border border-gray-300 rounded-md text-sm"
            />
          </div>

          <div className="text-gray-700">
            <span className="font-semibold">{formatClockTime(startAt)}</span>
            <span className="text-gray-400 mx-1">→</span>
            <span className="font-semibold">{formatClockTime(serveAt)}</span>
            <span className="text-gray-500 ml-2">
              · {formatDurationMinutes(totalMinutes)} · {completedCount}/{steps.length}
            </span>
          </div>

          <div className="flex flex-wrap gap-1.5">
            {Object.entries(CATEGORY_META).map(([key, meta]) => (
              <span key={key} className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full border ${meta.badge}`}>
                {meta.label}
              </span>
            ))}
          </div>
        </div>

        {warning && (
          <p className="mt-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5">
            {warning}
          </p>
        )}
      </div>

      {viewMode === 'timeline' ? (
        <>
          <div className="xl:hidden">{activeTimersPanel}</div>

          <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_320px] gap-6">
          <div className="bg-white rounded-xl shadow-lg p-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Timeline</h2>
              {sessionActive && (
                <span className="text-sm font-semibold text-amber-700 bg-amber-100 px-3 py-1 rounded-full">
                  NOW {formatClockTime(now)}
                </span>
              )}
            </div>

            <div className="grid grid-cols-[72px_minmax(0,1fr)] gap-4">
              <div className="relative">
                <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-gray-200 -translate-x-1/2" />
                <div className="space-y-16 py-2">
                  {timelineMarkers.slice(0, 8).map((marker) => (
                    <div key={marker} className="relative text-xs font-semibold text-gray-500 text-center">
                      {marker}
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-4">
                {sessionActive && activeSteps.length > 0 && (
                  <div className="hidden xl:block">
                    <h3 className="text-sm font-bold uppercase tracking-wide text-gray-500 mb-3">
                      In progress ({activeSteps.length})
                    </h3>
                    <div className="space-y-3">
                      {activeSteps.map((step) => (
                        <TimelineStepCard
                          key={step.id}
                          step={step}
                          state={stepStates[step.id]}
                          now={now}
                          sessionActive={sessionActive}
                          onComplete={completeStep}
                        />
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <h3 className="text-sm font-bold uppercase tracking-wide text-gray-500 mb-3">
                    {sessionActive ? 'Coming up' : 'All steps'}
                  </h3>
                  <div className="space-y-3">
                    {(sessionActive ? upcomingSteps : previewUpcoming).map((step) => {
                      const waitReason = waitingSteps.some((item) => item.id === step.id)
                        ? getResourceBlockReason(step)
                        : null;

                      return (
                        <TimelineStepCard
                          key={step.id}
                          step={step}
                          state={stepStates[step.id]}
                          now={now}
                          sessionActive={sessionActive}
                          waitReason={waitReason}
                          onComplete={completeStep}
                        />
                      );
                    })}
                  </div>
                </div>

                <div className="rounded-xl border-2 border-dashed border-amber-300 bg-amber-50 p-4 text-center">
                  <p className="font-bold text-amber-900">Serve!</p>
                  <p className="text-sm text-amber-800">{formatClockTime(liveServeAt)}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="hidden xl:block">{activeTimersPanel}</div>

            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
              <h3 className="font-bold text-gray-900 mb-3">Next up</h3>
              <div className="space-y-2">
                {(sessionActive ? upcomingSteps : previewUpcoming).slice(0, 3).map((step) => (
                  <div key={step.id} className="flex items-start gap-2">
                    <span
                      className={`mt-1.5 h-2 w-2 rounded-full flex-shrink-0 ${
                        (CATEGORY_META[step.category] || CATEGORY_META.prep).dot
                      }`}
                    />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{step.label}</p>
                      <p className="text-xs text-gray-500">
                        {step.recipeTitle} · {formatDurationMinutes(step.duration)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <EquipmentPanel
              equipmentStatus={equipmentStatus}
              stepStates={stepStates}
              now={now}
            />
          </div>
        </div>
        </>
      ) : (
        <div className="bg-white rounded-xl shadow-lg p-5 sm:p-6">
          <h2 className="text-xl font-bold mb-4">Overview</h2>
          <div className="space-y-3">
            {anchoredSteps.map((step) => (
              <div
                key={step.id}
                className="flex flex-wrap items-center gap-3 border border-gray-200 rounded-lg p-3"
              >
                <span className="text-sm font-semibold text-gray-500 w-16">
                  {formatClockTime(step.plannedStartAt)}
                </span>
                <CategoryBadge category={step.category} />
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-gray-900">{step.label}</p>
                  <p className="text-sm text-gray-500">{step.recipeTitle}</p>
                </div>
                <span className="text-sm text-gray-500">{formatDurationMinutes(step.duration)}</span>
                <span className="text-xs font-semibold uppercase text-gray-400">
                  {stepStates[step.id]?.status || 'pending'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <details className="bg-white rounded-xl shadow-lg p-5 sm:p-6">
        <summary className="font-bold cursor-pointer">Parsed task details</summary>
        <div className="space-y-4 mt-4">
          {recipes.map((recipe) => (
            <div key={recipe.id} className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-semibold mb-2">{recipe.title}</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="py-2 pr-3">Step</th>
                      <th className="py-2 pr-3">Min</th>
                      <th className="py-2 pr-3">Category</th>
                      <th className="py-2">Equipment</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recipe.tasks.map((task) => (
                      <tr key={task.id || task.name} className="border-b border-gray-100">
                        <td className="py-2 pr-3">{task.label || task.name}</td>
                        <td className="py-2 pr-3">{task.duration}</td>
                        <td className="py-2 pr-3">{task.category || task.kind}</td>
                        <td className="py-2">
                          {Array.isArray(task.resources) && task.resources.length
                            ? task.resources.join(', ')
                            : task.resources || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      </details>
    </div>
  );
}

export default CookingSessionView;
